import { isTapeReading } from "./tradeHelpers";

export const ACCOUNT_TYPE_PERSONAL = "personal";
export const ACCOUNT_TYPE_PROP = "funded";
export const PROP_PHASE_EVALUATION = "evaluation";
export const PROP_PHASE_FUNDED = "funded";

export function isArchivedAccount(account) {
  return account?.archived === true;
}

export function splitAccountsByArchive(accounts) {
  return (accounts || []).reduce(
    (result, account) => {
      if (isArchivedAccount(account)) {
        result.archived.push(account);
      } else {
        result.active.push(account);
      }
      return result;
    },
    { active: [], archived: [] }
  );
}

export function getPropPhase(account) {
  if (account?.accountType !== ACCOUNT_TYPE_PROP) return null;
  return account.propPhase === PROP_PHASE_EVALUATION
    ? PROP_PHASE_EVALUATION
    : PROP_PHASE_FUNDED;
}

export function getAccountPhaseLabel(account) {
  if (account?.accountType !== ACCOUNT_TYPE_PROP) return "Personal";
  if (getPropPhase(account) === PROP_PHASE_EVALUATION) {
    return account?.evaluationStatus === "passed" ? "Evaluation passed" : "Evaluation";
  }
  return "Funded";
}

export function sanitizeEvaluationTarget(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round(parsed * 100) / 100;
}

export function buildAccountRecord(accountData, created = new Date().toISOString()) {
  const accountType =
    accountData?.accountType === ACCOUNT_TYPE_PROP
      ? ACCOUNT_TYPE_PROP
      : ACCOUNT_TYPE_PERSONAL;
  const propPhase =
    accountType === ACCOUNT_TYPE_PROP &&
    accountData?.propPhase === PROP_PHASE_EVALUATION
      ? PROP_PHASE_EVALUATION
      : accountType === ACCOUNT_TYPE_PROP
        ? PROP_PHASE_FUNDED
        : null;
  const evaluationTarget =
    propPhase === PROP_PHASE_EVALUATION
      ? sanitizeEvaluationTarget(accountData?.evaluationTarget)
      : null;

  if (propPhase === PROP_PHASE_EVALUATION && evaluationTarget === null) {
    throw new Error("Evaluation profit target must be greater than $0");
  }

  const initialBalance = Number(accountData?.balance);
  if (!Number.isFinite(initialBalance) || initialBalance <= 0) {
    throw new Error("Balance must be greater than $0");
  }

  return {
    name: String(accountData?.name || "").trim(),
    balance: 0,
    accountType,
    ...(propPhase ? { propPhase } : {}),
    ...(evaluationTarget !== null ? { evaluationTarget } : {}),
    initialBalance: Math.round(initialBalance * 100) / 100,
    evaluationStatus:
      propPhase === PROP_PHASE_EVALUATION ? "active" : null,
    archived: false,
    created,
  };
}

export function isEvaluationTrade(entry) {
  if (!entry || entry.isDeposit || entry.isPayout || entry.isResetExcluded) {
    return false;
  }
  if (isTapeReading(entry)) return false;
  return entry.pnl !== undefined && entry.pnl !== null && entry.pnl !== "";
}

export function getEvaluationProfit(entries) {
  const total = (entries || [])
    .filter(isEvaluationTrade)
    .reduce((sum, entry) => sum + (Number(entry.pnl) || 0), 0);
  return Math.round(total * 100) / 100;
}

export function getEvaluationProgress(account, entries) {
  const target = sanitizeEvaluationTarget(account?.evaluationTarget) || 0;
  const profit = getEvaluationProfit(entries);
  const percent = target > 0
    ? Math.min(100, Math.max(0, (profit / target) * 100))
    : 0;

  return {
    target,
    profit,
    remaining: Math.max(0, Math.round((target - profit) * 100) / 100),
    percent,
    targetReached: target > 0 && profit >= target,
  };
}
